import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { toApiMutationError } from '../lib/error.js';
import type { DeviceSettings, DeviceSettingsUpdate } from '../types/device-settings';

const getDeviceSettings = async (id: string): Promise<DeviceSettings> => {
  try {
    const { data } = await apiClient.get<DeviceSettings>(`/devices/${id}/settings`);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to fetch device settings');
  }
};

const updateDeviceSettings = async (params: {
  id: string;
  settings: DeviceSettingsUpdate;
}): Promise<void> => {
  const { id, settings } = params;
  try {
    const { data } = await apiClient.post(`/devices/${id}/settings`, settings);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw toApiMutationError(error, 'Failed to update device settings');
  }
};

export const useDeviceSettings = (id: string | null | undefined, enabled?: boolean) => {
  return useQuery<DeviceSettings>({
    queryKey: ['device', 'settings', id],
    queryFn: () => getDeviceSettings(id as string),
    enabled: !!id && (enabled ?? true),
    staleTime: 30_000,
  });
};

export const useUpdateDeviceSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDeviceSettings,
    onSuccess: (_res, vars) => {
      // Only this key: the cache holds the server's per-viewer view, so spreading the payload would
      // put client-sent location fields and explicit `undefined`s into it. With no observer mounted
      // the invalidation below only marks the query stale, and the next reader renders the old value.
      const mode = vars.settings.defaultBroadbandSuppressionMode;
      if (mode !== undefined) {
        queryClient.setQueryData<DeviceSettings>(['device', 'settings', vars.id], (old) =>
          old && old.defaultBroadbandSuppressionMode !== mode
            ? { ...old, defaultBroadbandSuppressionMode: mode }
            : old,
        );
      }
      queryClient.invalidateQueries({ queryKey: ['device', 'settings', vars.id] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'tag-autocomplete',
      });
    },
  });
};
